import React, { type ReactNode } from "react";
import { type Merge } from "type-fest";
import { useTranslate } from "../../services";
import { css, cx } from "../../styled-system/css";
import { type DropDownListProperties } from "./drop-down-list";

export type PageSizeSelectorProperties = Merge<
  DropDownListProperties,
  {
    children?: never;
    className?: React.ComponentProps<"div">["className"];
    /**
     * **既定のページサイズ**
     *
     * @default `selectablePageSizes[0]`
     */
    defaultValue?: number;
    /**
     * **選択肢のフォーマット関数**
     * 選択肢をどのように表示するかを指定する。
     */
    format?: { (value: number): ReactNode };
    onChange: { (value: number, event: React.ChangeEvent<HTMLSelectElement>): void };
    /** ページサイズの選択肢 */
    options: Iterable<number>;
    /** 現在のページサイズ */
    value: number;
  }
>;

export function PageSizeSelector({
  className,
  defaultValue,
  format,
  onChange,
  options,
  value,
  ...properties
}: PageSizeSelectorProperties) {
  const translate = useTranslate();

  return (
    <div
      className={cx(
        css({
          backgroundColor: "white",
          borderColor: "#E4E4E7",
          borderRadius: "6px",
          borderStyle: "solid",
          borderWidth: "1px",
          minHeight: "max-content",
          minWidth: "max-content",
          position: "relative",

          _before: {
            content: '""',
            position: "absolute",
            top: "calc(50% - 5px)",
            right: "16px",
            width: "7px",
            height: "7px",
            borderColor: "#18181B",
            borderStyle: "solid",
            borderBottomWidth: "1px",
            borderRightWidth: "1px",
            transform: "rotate(45deg)",
            pointerEvents: "none",
          },
        }),
        className,
      )}
    >
      <select
        {...properties}
        className={css({
          appearance: "none",
          cursor: "context-menu",
          fontSize: "14px",
          fontWeight: 500,
          lineHeight: 1,
          paddingBottom: "12px",
          paddingLeft: "12px",
          paddingRight: "32px",
          paddingTop: "12px",
        })}
        onChange={function (event) {
          const value = Number.parseInt(event.target.value, 10);
          if (Number.isSafeInteger(value) && value > 0) {
            onChange?.(value, event);
          }
        }}
        value={String(value ?? defaultValue)}
      >
        {Array.from(options)
          .filter(function (option) {
            return Number.isSafeInteger(option) && option > 0;
          })
          .map(function (option) {
            return (
              <option key={option} value={option}>
                {format?.(option) ?? translate("%ItemCount.0%", { $: { count: option } })}
              </option>
            );
          })}
      </select>
    </div>
  );
}
